import { useContext, useState } from 'react'
import { ItemsContext } from '../ItemsRoot'
import { TimeAnalysisRow, VisitFilter, createTimeAnalysisRows } from './timeAnalysisModel'
import { Progress, Row as GridRow, Col, ProgressProps, Space } from 'antd'
import { percentageDisplayWhole } from '../../displayFormatters'
import { roundToPrecision } from '../../../helpers/math'
import { green, red, yellow } from '@ant-design/colors'
import ExpandIcon from '../../common/ExpandIcon'

const nestIndent = 16
export type Props = {
  enabledWorkareaIds: number[]
  visitFilters: VisitFilter[]
}

const Summary = ({row}: {row: TimeAnalysisRow}) => {
  const percentage = row.percentage !== undefined && <>{percentageDisplayWhole(row.percentage)} / </>
  if (row.estimatedHours >= row.actualHours) {
    return <>{percentage}{roundToPrecision(row.estimatedHours - row.actualHours, 2)} hours remaining</>
  }

  return <>{percentage}{roundToPrecision(row.actualHours - row.estimatedHours, 2)} hours over</>
}

const getStrokeColor = (row: TimeAnalysisRow): ProgressProps['strokeColor'] => {
  if (row.percentage === undefined || row.percentage > 1) {
    return red.primary
  } else if (row.percentage < 1) {
    return green.primary
  } else {
    return yellow.primary
  }
}

const Row = ({row, isExpanded, onExpand, indent = 0}: {row: TimeAnalysisRow, isExpanded?: boolean, onExpand?: () => void, indent?: number}) => {
  return <GridRow gutter={16} style={row.disabled ? { color: '#ccc' } : {}}>
    <Col span={4} style={{ textAlign: 'left' }}>
      <Space size="large">
        <div style={{paddingLeft: indent}}>{row.workareaName}</div>
        {row.isSummaryRow && <ExpandIcon isExpanded={isExpanded ?? false} onClick={onExpand} /> }
      </Space>
    </Col>
    <Col span={1} style={{ textAlign: 'right' }}>{roundToPrecision(row.actualHours, 2)}h</Col>
    <Col span={18}>
      <Progress
        key={row.key}
        percent={row.disabled ? 0 : (row.percentage ?? row.normalizedPercentage) * 100}
        success={(row.percentage ?? 0) > 1 ? { percent: row.normalizedPercentage * 100, strokeColor: yellow.primary } : {}}
        status="normal"
        strokeColor={getStrokeColor(row)}
        showInfo={false}
      />
      <small><Summary row={row} /></small>
    </Col>
    <Col span={1}>{roundToPrecision(row.estimatedHours, 2)}h</Col>
  </GridRow>
}

const TimeAnalysisGroup = ({row, indent = 0}: {row: TimeAnalysisRow, indent?: number}) => {
  const [isOpen, setIsOpen] = useState(false)
  if (row.children) {
    return <>
      <Row row={row} onExpand={() => setIsOpen(!isOpen)} isExpanded={isOpen} indent={indent}/>
      {isOpen && row.children?.map(row => <TimeAnalysisGroup key={row.key} row={row} indent={indent + nestIndent} />)}
    </>
  } else {
    return <Row key={row.key} row={row} indent={indent} />
  }
}

export const TimeAnalysis = ({ enabledWorkareaIds, visitFilters }: Props) => {
  const { changeSet, jobItemsQuery, jobData } = useContext(ItemsContext)
  
  const row = createTimeAnalysisRows(
    jobItemsQuery.data?.estimatedItems ?? [],
    jobItemsQuery.data?.actualItems ?? [],
    changeSet,
    jobData.workareas,
    enabledWorkareaIds,
    visitFilters
  )

  return <TimeAnalysisGroup row={row} />
}
export default TimeAnalysis